// Progress S-Curve — the Overview VoltaGrid tab's version of the "Voltagrid
// Progress S-Curves" workbook's dashboard: Plan, Actual and Forecast
// cumulative percent complete by month, on one 0–100% axis, with the
// workbook's month table beneath. Sits right after the Labor Hours graph.
//
// Progress is purely labor hours (Seth, 10/8). Nothing here is pasted in
// month by month (the workbook takes a P6 spread): the lines are the Labor
// Hours graph's figures by month, read twice — posted hours plus the Burn
// Tool Labor hours of the selected version (Forecast), and of the job's first
// version, the Day Zero build (Plan) — and the posted hours through the
// forecast month (Actual). The math is in helpers/progressCurve.js.

import React, { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend } from 'chart.js';
import { loadUserData, loadJobVersions, pNum } from '../db/company';
import { jobCostDetailAtom } from '../store/company';
import { progressCurveFrom, planVersionOf, laborHoursCurveModel } from '../helpers/progressCurve';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);

// The job's version list and the plan version's rows, once per job for the
// session: the Day Zero build doesn't change under a reader.
const jobVersionsCache = {};
const planRowsCache = {};

const SERIES = {
    plan: { color: '#94a3b8', label: 'Plan cum %' },
    actual: { color: '#d97706', label: 'Actual cum %' },
    forecast: { color: '#2563eb', label: 'Forecast cum %' },
};
const pct = (v, digits = 1) => (v == null ? '-' : `${(pNum(v) * 100).toFixed(digits)}%`);
const hrs = (v) => { const n = Math.round(pNum(v)); return n ? n.toLocaleString('en-US') : '-'; };

/* ─────────────────────────── the chart ─────────────────────────── */
function ProgressChart({ palette, model }) {
    const rows = model.rows;
    const firstFuture = rows.findIndex((r) => r.future);
    const line = (key, data, extra = {}) => ({
        label: SERIES[key].label,
        data,
        borderColor: SERIES[key].color,
        backgroundColor: SERIES[key].color,
        borderWidth: 2,
        tension: 0.25,
        pointRadius: 2.5,
        pointHoverRadius: 5,
        pointBorderWidth: 2,
        pointBorderColor: SERIES[key].color,
        spanGaps: false,
        ...extra,
    });
    const datasets = [
        ...(model.hasPlan ? [line('plan', rows.map((r) => r.planPct), { borderDash: [4, 3], pointRadius: 0 })] : []),
        line('forecast', rows.map((r) => r.forecastPct), {
            // Solid through the forecast month, dashed with hollow points after.
            segment: { borderDash: (ctx) => (firstFuture >= 0 && ctx.p1DataIndex >= firstFuture ? [6, 4] : undefined) },
            pointBackgroundColor: rows.map((r) => (r.future ? '#fff' : SERIES.forecast.color)),
        }),
        line('actual', rows.map((r) => r.actualPct), { borderWidth: 2.5, pointRadius: 3 }),
    ];
    const data = { labels: rows.map((r) => r.label), datasets };
    const options = {
        responsive: true, maintainAspectRatio: false, devicePixelRatio: 2,
        interaction: { mode: 'index', intersect: false },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 9 }, color: palette.muted, maxRotation: 0, autoSkip: true } },
            y: { min: 0, max: 1, grid: { color: '#f1f5f9' }, border: { display: false }, ticks: { font: { size: 9 }, color: palette.muted, callback: (v) => `${Math.round(v * 100)}%`, stepSize: 0.25 } },
        },
        plugins: {
            legend: { display: true, position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 2, usePointStyle: false, font: { size: 9.5 }, color: palette.muted, padding: 14 } },
            tooltip: {
                backgroundColor: '#fff', titleColor: palette.text, bodyColor: palette.text, footerColor: palette.muted, borderColor: palette.border, borderWidth: 1, padding: 10, cornerRadius: 6,
                callbacks: {
                    title: (items) => { const r = rows[items[0]?.dataIndex]; return r ? `${r.label}${r.future ? ' — forecast' : r.current ? ' — actual to date + forecast' : ''}` : ''; },
                    label: (item) => `${item.dataset.label}: ${pct(item.parsed.y)}`,
                    footer: (items) => {
                        const r = rows[items[0]?.dataIndex];
                        if (!r) return '';
                        const parts = [`Forecast cum hrs: ${hrs(r.forecastCum)}`];
                        if (model.hasPlan) parts.unshift(`Plan cum hrs: ${hrs(r.planCum)}`);
                        if (r.actualCum != null) parts.push(`Actual cum hrs: ${hrs(r.actualCum)}`);
                        return parts;
                    },
                },
            },
        },
    };
    return (
        <div style={{ height: 230, marginBottom: 14 }}>
            <Line data={data} options={options} />
        </div>
    );
}

/* ─────────────────────────── the table ─────────────────────────── */
function ProgressTable({ palette, model }) {
    const INK = '#1e293b';
    const th = { background: INK, color: '#fff', fontSize: 7.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' };
    const thL = { ...th, textAlign: 'left', position: 'sticky', left: 0, zIndex: 1 };
    const td = { fontSize: 11, color: palette.text, padding: '5px 8px', borderBottom: `1px solid ${palette.border}`, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
    const tdL = { ...td, textAlign: 'left', position: 'sticky', left: 0, background: palette.card, fontWeight: 600, zIndex: 1 };
    const futureBg = '#f8fafc';
    const lines = [
        ...(model.hasPlan ? [{ key: 'planPct', label: 'Plan cum %', get: (r) => pct(r.planPct), total: pct(1, 0) }] : []),
        { key: 'actualPct', label: 'Actual cum %', get: (r) => pct(r.actualPct), total: pct(model.forecastTotal > 0 ? model.actualToDate / model.forecastTotal : 0) },
        { key: 'forecastPct', label: 'Forecast cum %', get: (r) => pct(r.forecastPct), total: pct(1, 0) },
    ];
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                    <tr>
                        <th style={thL}>Progress</th>
                        {model.rows.map((r) => (
                            <th key={r.key} style={{ ...th, color: r.future ? '#cbd5e1' : '#fff' }} title={r.future ? 'Forecast' : r.current ? 'Actual to date plus forecast' : 'Actual'}>{r.label}</th>
                        ))}
                        <th style={{ ...th, borderLeft: '1px solid #475569' }}>At completion</th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((ln) => (
                        <tr key={ln.key}>
                            <td style={tdL}>{ln.label}</td>
                            {model.rows.map((r) => (
                                <td key={r.key} style={{ ...td, background: r.future ? futureBg : 'transparent' }}>{ln.get(r)}</td>
                            ))}
                            <td style={{ ...td, fontWeight: 700, borderLeft: `1px solid ${palette.border}` }}>{ln.total}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ─────────────────────────── the section ─────────────────────────── */
function VoltaGridProgressCurve({ palette, versionInfo, jobUserData, selectedJob, selectedVersion }) {
    const [costRows] = useAtom(jobCostDetailAtom);

    // The plan version: the job's first forecast with a cost spread (the Day
    // Zero build), off the job's own version list. When that is the version
    // on screen the plan and forecast are one curve.
    const [jobVersions, setJobVersions] = useState(null);
    useEffect(() => {
        if (!selectedJob) { setJobVersions(null); return undefined; }
        if (jobVersionsCache[selectedJob]) { setJobVersions(jobVersionsCache[selectedJob]); return undefined; }
        let dead = false;
        setJobVersions(null);
        loadJobVersions({ id: selectedJob })
            .then((res) => {
                const rows = Array.isArray(res?.data) ? res.data : [];
                jobVersionsCache[selectedJob] = rows;
                if (!dead) setJobVersions(rows);
            })
            .catch(() => { if (!dead) setJobVersions([]); });
        return () => { dead = true; };
    }, [selectedJob]);
    const planVersion = useMemo(() => planVersionOf(jobVersions), [jobVersions]);
    const planIsSelected = !!planVersion && String(planVersion.Version_GUID) === String(selectedVersion);
    const [planRows, setPlanRows] = useState(null);
    useEffect(() => {
        if (!selectedJob || !planVersion || planIsSelected) { setPlanRows(null); return undefined; }
        const cacheKey = `${selectedJob}|${planVersion.Version_GUID}`;
        if (planRowsCache[cacheKey]) { setPlanRows(planRowsCache[cacheKey]); return undefined; }
        let dead = false;
        setPlanRows(null);
        loadUserData({ id: selectedJob, forecastGuid: planVersion.Version_GUID, live: false })
            .then((res) => {
                const rows = Array.isArray(res?.data) ? res.data : [];
                planRowsCache[cacheKey] = rows;
                if (!dead) setPlanRows(rows);
            })
            .catch(() => { if (!dead) setPlanRows([]); });
        return () => { dead = true; };
    }, [selectedJob, planVersion, planIsSelected]);

    const ready = Array.isArray(costRows) && Array.isArray(jobUserData);
    const forecastCurve = useMemo(() => (ready ? laborHoursCurveModel({ jobCostDetail: costRows, userData: jobUserData, versionInfo }) : null), [ready, costRows, jobUserData, versionInfo]);
    const planCurve = useMemo(() => {
        if (!ready || !planVersion) return null;
        if (planIsSelected) return forecastCurve;
        if (!planRows) return null;
        return laborHoursCurveModel({ jobCostDetail: costRows, userData: planRows, versionInfo: planVersion });
    }, [ready, planVersion, planIsSelected, forecastCurve, planRows, costRows]);
    const model = useMemo(() => (forecastCurve ? progressCurveFrom(forecastCurve, planCurve) : null), [forecastCurve, planCurve]);

    const asOf = versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime).format('M/D/YY') : moment().format('M/D/YY');
    const actualPct = model && model.forecastTotal > 0 ? model.actualToDate / model.forecastTotal : null;
    const planAtDate = model && model.hasPlan && model.dataMonth ? (model.rows.find((r) => r.key === model.dataMonth) || {}).planPct : null;

    return (
        <div className="vg-progress-section" style={{ background: palette.card, borderRadius: 10, border: `1px solid ${palette.border}`, boxShadow: '0 4px 16px rgba(15,23,42,.06)', padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: 11, fontWeight: 700, color: palette.text, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>Progress S-Curve</h3>
                    <span style={{ fontSize: 10, color: palette.muted }}>as of {asOf}</span>
                </div>
                {model && (
                    <div style={{ display: 'flex', gap: 14, fontSize: 10, color: palette.muted }}>
                        {model.hasPlan && planAtDate != null && <span>Plan to date <b style={{ color: palette.text }}>{pct(planAtDate)}</b></span>}
                        <span>Actual to date <b style={{ color: palette.text }}>{pct(actualPct)}</b></span>
                        <span>Forecast hours <b style={{ color: palette.text }}>{hrs(model.forecastTotal)}</b></span>
                    </div>
                )}
            </div>

            {!ready ? (
                <div style={{ fontSize: 11, color: palette.muted }}>Loading the labor hours…</div>
            ) : !model || !model.rows.length ? (
                <div style={{ fontSize: 11, color: palette.muted }}>No labor hours have posted and none are forecast in the Burn Tool for this job.</div>
            ) : (
                <>
                    <ProgressChart palette={palette} model={model} />
                    <ProgressTable palette={palette} model={model} />
                </>
            )}
        </div>
    );
}

export default VoltaGridProgressCurve;
